import {
  Accordion,
  Alert,
  Badge,
  Button,
  Grid,
  Group,
  Text,
} from '@mantine/core'
import { observer } from 'mobx-react-lite'

import { showUpgradeInquiryModal, useUpgrade } from '#components/Modals'
import { OutsideAnchor } from '#components/OutsideAnchor'
import { PaidFeatureNote } from '#components/Settings/Org/PaidFeatureNote'
import { STRIPE_TIER } from '#components/Settings/Org/plans'
import { Toast } from '#components/Toast'
import { IOrg } from '#state/types'

import { PermissionMemberTable } from './PermissionMemberTable'
import { PermissionUserSelect } from './PermissionUserSelect'
import { PermissionConfig } from './util'

import styles from './PermissionAccordions.module.scss'

type PermissionAccordionsProps = {
  permissions: PermissionConfig[]
  org: IOrg
}

const ManagePlanNote = observer(function ManagePlanNote() {
  const { pending, startSelfServe } = useUpgrade()

  return (
    <Alert p={10}>
      <PaidFeatureNote
        inline
        features={[{ name: 'this feature' }]}
        fz="md"
        action={
          <Button onClick={startSelfServe} loading={pending}>
            Manage Plan
          </Button>
        }
      />
    </Alert>
  )
})

const UpgradeToast = ({ org }: { org: IOrg }) => {
  if (org.tier === STRIPE_TIER) return <ManagePlanNote />

  return (
    <Toast
      type="neutral"
      dismissable={false}
      message={
        <Group gap={0}>
          <Button
            onClick={() => showUpgradeInquiryModal(org)}
            variant="subtle"
            pl={0}
            pr={0}
          >
            Contact us
          </Button>
          <Text>&nbsp;to enable this feature</Text>
        </Group>
      }
    ></Toast>
  )
}

export const PermissionAccordions = observer(function PermissionAccordions({
  permissions,
  org,
}: PermissionAccordionsProps) {
  return permissions.map((permission) => {
    const permittedMembers = org.permittedMembers(permission.code)
    const memberInfo = permission.enabled ? (
      <>
        <PermissionUserSelect permissionCode={permission.code} org={org} />
        {permittedMembers.length > 0 && (
          <PermissionMemberTable
            permissionCode={permission.code}
            org={org}
            members={permittedMembers}
          />
        )}
      </>
    ) : (
      <UpgradeToast org={org} />
    )

    return (
      <Accordion
        classNames={{
          content: styles.accordion_content,
          item: styles.accordion_item,
        }}
        className={styles.accordion}
        key={permission.code}
      >
        <Accordion.Item value={permission.code}>
          <Accordion.Control>
            <Grid columns={6} align="center">
              <Grid.Col span={5}>
                <Text size="md" fw="bold">
                  {permission.title}
                </Text>
                <Text>
                  <Text span>{permission.description}</Text>
                  {permission.helpLink && (
                    <OutsideAnchor
                      href={permission.helpLink}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      px={5}
                    >
                      Learn More
                    </OutsideAnchor>
                  )}
                </Text>
              </Grid.Col>
              <Grid.Col span={1}>
                {!permission.enabled && (
                  <Group justify="center">
                    <Badge size="md" radius="md" color="gray">
                      Disabled
                    </Badge>
                  </Group>
                )}
              </Grid.Col>
            </Grid>
          </Accordion.Control>
          <Accordion.Panel className={styles.accordion_panel}>
            {memberInfo}
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>
    )
  })
})
